import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { ROUTES } from '@lsgo/lsgo-common/constants/routes';
import { isNewAuthFlowEnabled, startLsgoAuthenticationLogin } from 'utils/lsgoAuthenticationRedirectFlow';

/**
 * Options for useAuthCheck hook
 */
export interface UseAuthCheckOptions {
    requiresAuth?: boolean;
    allowExpired?: boolean;
    embed?: boolean;
    redirectTo?: string;
}

/**
 * Centralized authentication and expiration check hook
 * Returns a redirect component if auth fails, or null if auth passes
 */
export const useAuthCheck = ({
    requiresAuth = true,
    allowExpired = false,
    embed = false,
    redirectTo,
}: UseAuthCheckOptions = {}): React.ReactElement | null => {
    const location = useLocation();
    const auth = useAuth({ embed });
    const startedNewAuthRedirect = React.useRef(false);

    // embed implies "always allow", so it must also imply "never start the
    // new-auth redirect" — computed here (not via an early return before the
    // hooks below) because every hook in this function must run on every
    // render, in the same order, regardless of `embed`/auth state.
    const needsAuth = !embed && requiresAuth && !auth.isAuthenticated;
    const useNewAuthFlow = needsAuth && isNewAuthFlowEnabled();

    // Feature-flagged (localStorage `lsgo_new_auth_enabled`) real login flow
    // against lsgo-authentication — see utils/lsgoAuthenticationRedirectFlow.js.
    // This redirects OFF this origin entirely (lsgo-authentication runs on
    // its own port), so unlike the legacy branch below it can't be a plain
    // <Navigate> — it's a real browser navigation, fired once from an
    // effect, with this hook rendering nothing while it's in flight. Both
    // flows stay fully intact side by side: flip the flag off and every
    // route goes right back to the legacy branch, unchanged.
    React.useEffect(() => {
        if (useNewAuthFlow && !startedNewAuthRedirect.current) {
            startedNewAuthRedirect.current = true;
            startLsgoAuthenticationLogin(location.pathname + location.search);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [useNewAuthFlow]);

    // Public routes (embed mode) - always allow
    if (embed) {
        return null;
    }

    // Every caller of this hook treats a null return as "auth passed, render
    // the protected content" (see PrivateRoute.js, RouteGuard.js,
    // withAuthGuard.js — all do `if (authRedirect) return authRedirect;`).
    // window.location.href in the effect above doesn't unmount this tree
    // synchronously, so a null here would let the real protected page (and
    // its data fetches) render for a moment against an unauthenticated
    // session before the browser navigates away. Return a real element
    // instead so callers render this in place of the protected content
    // while the redirect is in flight.
    if (useNewAuthFlow) {
        return <div />;
    }

    // Check authentication
    if (needsAuth) {
        const loginPath = redirectTo || `/?redirectUri=${encodeURIComponent(location.pathname + location.search)}`;
        return <Navigate to={loginPath} replace />;
    }

    // Check account expiration
    if (requiresAuth && !allowExpired && auth.isExpired && !location.pathname.includes(ROUTES.TEACHER.PROFILE.path)) {
        return <Navigate to={ROUTES.TEACHER.PROFILE.path} replace />;
    }

    // All auth checks passed
    return null;
};
